import styled from "@emotion/styled"
import {
	breakpoint,
	spacingFluidLg,
	spacingFluidMd,
	spacingFluidXl,
} from "@porsche-design-system/components-react/emotion"
import type {
	ActionItem as ActionItemType,
	FinancingView,
	InventoryInformationView,
	OverviewSummaryView,
} from "@slfinrtl/ui-bff-routes"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { Flex, Grid, GridItem, mapGapToSpacingFluid, Skeleton } from "src/components"
import type { FlexProps } from "src/components"
import { useMediaQueryWidth } from "src/hooks"
import type { VehicleDetailsStage } from "src/screens/vehicleDetails/common/VehicleDetailsStage"
import { useVehicleDetailsContext } from "src/screens/vehicleDetails/context/VehicleDetailsContext"
import type { ColorsProps } from "../../hooks/useColors/useColors"
import { OverviewSummaryImages } from "../overviewSummaryImages/OverviewSummaryImages"
import { ConditionList } from "./conditionList/ConditionList"
import { InventoryInformationList } from "./inventoryInformationList/InventoryInformationList"
import { ListingList } from "./listingList/ListingList"
import { ModelInformationList } from "./modelInformationList/ModelInformationList"

interface OverviewSummaryProps {
	colors: ColorsProps | null
	financingView?: FinancingView
	imageEdit?: ActionItemType
	inventoryInformationView?: InventoryInformationView | null
	isFinancingEnabled: boolean
	isOnlineOrderingEnabled: boolean
	onEditClick: (id: VehicleDetailsStage) => void
	onOpenGalleryClick: () => Promise<void>
	view: OverviewSummaryView
}

export const OverviewSummary = (props: OverviewSummaryProps) => {
	const {
		colors,
		financingView,
		imageEdit,
		inventoryInformationView,
		isFinancingEnabled,
		isOnlineOrderingEnabled,
		onEditClick,
		onOpenGalleryClick,
		view,
	} = props
	const { colorsEdit, commissionNumber, imageCount, images, newCarOrderId, openGallery, orderedAs, vin } = view
	const { inventoryId } = useVehicleDetailsContext()

	const isSmall = useMediaQueryWidth({ max: "l" })
	const columnGap = isSmall ? spacingFluidLg : spacingFluidXl
	const rowGap: FlexProps["$gap"] = "md"

	return (
		<Grid
			$columnGap={{ base: spacingFluidMd, l: spacingFluidXl, s: spacingFluidLg }}
			$rowGap={mapGapToSpacingFluid[rowGap]}
		>
			<GridItem size={{ base: 12, l: 4, m: 6 }}>
				<OverviewSummaryImages
					images={images}
					totalImageCount={imageCount}
					imageEdit={imageEdit}
					inventoryId={inventoryId}
					onOpenGalleryClick={onOpenGalleryClick}
					openGallery={openGallery}
				/>
			</GridItem>
			<GridItem size={{ base: 12, l: 4, m: 6 }}>
				<Flex $gap={rowGap} $direction="column">
					{inventoryInformationView ? (
						<ConditionList view={inventoryInformationView} onEditClick={onEditClick} />
					) : inventoryInformationView === undefined ? (
						<Skeleton.List entries={5} withHeader />
					) : null}
					<ModelInformationList colors={colors} colorsEdit={colorsEdit} />
				</Flex>
			</GridItem>
			<GridItem size={{ base: 12, l: 4, m: 12 }}>
				<InnerGrid $columnGap={columnGap} $rowGap={mapGapToSpacingFluid[rowGap]}>
					<ListingList
						porscheApproved={view.porscheApproved}
						inventoryInformationView={inventoryInformationView}
						financingView={financingView}
						isFinancingEnabled={isFinancingEnabled}
						isOnlineOrderingEnabled={isOnlineOrderingEnabled}
						onEditClick={onEditClick}
					/>
					<InventoryInformationList
						inventoryInformationView={inventoryInformationView}
						vin={vin}
						commissionNumber={commissionNumber}
						newCarOrderId={newCarOrderId}
						orderedAs={orderedAs}
						onEditClick={onEditClick}
					/>
				</InnerGrid>
			</GridItem>
		</Grid>
	)
}

const InnerGrid = styled("div", { shouldForwardProp })<{ $columnGap: string; $rowGap: string }>`
	display: grid;
	gap: ${({ $rowGap }) => $rowGap};
	column-gap: ${({ $columnGap }) => $columnGap};

	@media (min-width: ${breakpoint.m}px) and  (max-width: ${breakpoint.l}px) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
`
